import { Box } from "@mui/material";
import DOMPurify from "dompurify";
import React from "react";

/**
 * SafeHtml компонент для безпечного виведення HTML контенту з backend
 * Використовує DOMPurify для санітизації HTML і запобігання XSS атакам
 *
 * @param {string} html - HTML строка для виведення
 * @param {object} sx - MUI sx стилі для контейнера
 * @param {string} component - HTML тег для контейнера (за замовчуванням 'div')
 * @param {object} purifyConfig - Додаткові налаштування для DOMPurify
 * @param {object} ...props - Додаткові пропси для Box компонента
 */
const SafeHtml = ({
  html,
  sx = {},
  component = "div",
  purifyConfig = {},
  ...props
}) => {
  // Конфігурація за замовчуванням для DOMPurify
  const defaultConfig = {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "blockquote",
      "pre",
      "code",
      "a",
      "img",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "span",
      "div",
      "b",
      "i",
    ],
    ALLOWED_ATTR: [
      "href",
      "target",
      "rel",
      "src",
      "alt",
      "width",
      "height",
      "class",
      "id",
      "style",
    ],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    ...purifyConfig,
  };

  // Санітизація HTML контенту
  const sanitizedHtml = React.useMemo(() => {
    if (!html || typeof html !== "string") {
      return "";
    }
    return DOMPurify.sanitize(html, defaultConfig);
  }, [html, purifyConfig]);

  // Якщо немає контенту, повертаємо null
  if (!sanitizedHtml) {
    return null;
  }

  return (
    <Box
      component={component}
      sx={{
        // Базові стилі для правильного відображення HTML контенту
        "& p": {
          margin: "0 0 16px 0",
          "&:last-child": {
            marginBottom: 0,
          },
        },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          margin: "24px 0 16px 0",
          "&:first-child": {
            marginTop: 0,
          },
        },
        "& ul, & ol": {
          margin: "0 0 16px 0",
          paddingLeft: "24px",
        },
        "& li": {
          marginBottom: "8px",
        },
        "& blockquote": {
          margin: "16px 0",
          padding: "16px",
          borderLeft: "4px solid #e0e0e0",
          backgroundColor: "#f5f5f5",
        },
        "& pre": {
          backgroundColor: "#f5f5f5",
          padding: "16px",
          borderRadius: "4px",
          overflow: "auto",
          margin: "16px 0",
        },
        "& code": {
          backgroundColor: "#f5f5f5",
          padding: "2px 4px",
          borderRadius: "2px",
          fontFamily: "monospace",
        },
        "& a": {
          color: "primary.main",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
        "& img": {
          maxWidth: "100%",
          height: "auto",
        },
        "& table": {
          width: "100%",
          borderCollapse: "collapse",
          margin: "16px 0",
        },
        "& th, & td": {
          border: "1px solid #e0e0e0",
          padding: "8px 12px",
          textAlign: "left",
        },
        "& th": {
          backgroundColor: "#f5f5f5",
          fontWeight: "bold",
        },
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      {...props}
    />
  );
};

export default SafeHtml;
